import type { ReactNode } from "react";

import { DotsThreeIcon } from "@phosphor-icons/react";
import { DropdownMenu, IconButton } from "@radix-ui/themes";

export interface WidgetActionsMenuProps {
  widgetId: string;
  onRemove: () => void;
  /** Widget-specific items prepended before Remove, matching DataWidget. */
  menuItems?: ReactNode;
}

/**
 * Per-widget actions dropdown (remove).
 *
 * Rendered on top of the Widget header. Drag isolation relies on
 * react-grid-layout's `dragConfig.handle`, which is configured with
 * `[data-widget-drag-handle]`, so a mousedown on this trigger cannot start a
 * drag: the engine never sees this button as a handle target. We therefore
 * avoid capture-phase event stops that would fight Radix's own trigger
 * handlers. The `data-menu-root` attribute tags the subtree for downstream
 * consumers (defence-in-depth cancel selectors, telemetry, etc.). `Remove`
 * renders with Radix's destructive `color="red"` variant to signal
 * irreversibility. Radix Themes owns portal theming, so the menu content
 * inherits the appearance from the nearest `<Theme>` without any additional
 * wrapping.
 */
export const WidgetActionsMenu = ({
  widgetId,
  onRemove,
  menuItems,
}: WidgetActionsMenuProps) => {
  return (
    <span data-menu-root className="inline-flex">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <IconButton
            type="button"
            aria-label={`Widget actions for ${widgetId}`}
            variant="ghost"
            color="gray"
            size="1"
          >
            <DotsThreeIcon size={14} aria-hidden />
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end" size="1" variant="soft">
          {menuItems ? (
            <>
              {menuItems}
              <DropdownMenu.Separator />
            </>
          ) : null}
          <DropdownMenu.Item color="red" onSelect={onRemove}>
            Remove
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </span>
  );
};
